'use client';

import { useEffect, useState } from 'react';
import { APP_VERSION, BUILD_ID } from '@/lib/build-info';

type VersionJson = {
  version?: string;
  buildId?: string;
  buildTime?: string;
};

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

function isNewerVersion(
  currentVersion: string,
  currentBuildId: string,
  deployed: VersionJson
): boolean {
  const deployedVersion = (deployed.version ?? '').trim();
  const deployedBuildId = (deployed.buildId ?? '').trim();
  if (deployedBuildId && currentBuildId && deployedBuildId !== currentBuildId) {
    return true;
  }
  if (deployedVersion && deployedVersion !== currentVersion) {
    return true;
  }
  return false;
}

export function useUpdateCheck(enabled = true): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const check = async () => {
      try {
        const base = window.location.origin;
        const res = await fetch(`${base}/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { Pragma: 'no-cache' },
        });
        if (!res.ok) return;
        const data = (await res.json()) as VersionJson;
        if (isNewerVersion(APP_VERSION, BUILD_ID, data)) {
          setUpdateAvailable(true);
        }
      } catch {
        // ignore (offline, CORS, etc.)
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);

  return updateAvailable;
}
